/**
 * Social leaderboard queries — `/social/leaderboard` data for `/social`'s
 * Leaderboard surface.
 *
 * `metric` / `category` / `timePeriod` are part of every query key so changing
 * any of them auto-invalidates the cache. Each leaderboard refetches on a 60s
 * interval — server data updates roughly every 5 minutes, so a 1-minute cadence
 * is sufficient.
 */

import { useQuery, type QueryClient, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';

import { fetchServerSide } from 'app-migration/api/fetchServerSide';
import { createApiClient } from 'lib/api/createApiClient';
import { useAuthStore } from 'lib/stores/authStore';
import type { GetSocialLeaderboardResponse } from 'models/reqres';
import config from 'utils/config';
import type { LeaderboardTimePeriod } from 'utils/time';

const api = createApiClient({ baseURL: `${config.exchangeApiUrl}/v1` });

const REFETCH_INTERVAL_MS = 60 * 1000;
const STALE_TIME_MS = 30 * 1000;

export const SOCIAL_LEADERBOARD_LIMIT = 20;
export const PERPS_LEADERBOARD_LIMIT = 60;

/** Server-side metric name for the `/social/leaderboard` endpoint. */
export type LeaderboardMetricName = 'volume' | 'projected_pnl' | 'num_markets_traded';

export interface SocialLeaderboardParams {
    metric: LeaderboardMetricName;
    limit: number;
    category?: string;
    timePeriod?: LeaderboardTimePeriod;
}

/**
 * Plain-object shape consumed by `MetricLeaderboard`. No class prototype, so
 * the data can cross the RSC boundary during SSR hydration.
 */
export interface SocialLeaderboardEntry {
    nickname: string;
    rank: number;
    value: number;
    isAnonymous: boolean;
    profileImagePath?: string;
}

export interface SocialLeaderboardUserRank {
    isRanked: boolean;
    rank: number | null;
    value: number | null;
}

interface GetSocialLeaderboardRankResponse {
    ranked: boolean;
    rank_item?: {
        rank?: number;
        value?: number;
    } | null;
}

export type SocialLeaderboardRankParams = Omit<SocialLeaderboardParams, 'limit'>;

export const socialLeaderboardKeys = {
    all: ['socialLeaderboard'] as const,
    list: (params: SocialLeaderboardParams) =>
        [
            ...socialLeaderboardKeys.all,
            params.metric,
            params.limit,
            params.category ?? null,
            params.timePeriod ?? null,
        ] as const,
    userRank: (userId: string, params: SocialLeaderboardRankParams) =>
        [
            ...socialLeaderboardKeys.all,
            'userRank',
            userId,
            params.metric,
            params.category ?? null,
            params.timePeriod ?? null,
        ] as const,
};

async function fetchSocialLeaderboard(
    params: SocialLeaderboardParams,
    signal?: AbortSignal,
): Promise<SocialLeaderboardEntry[]> {
    const queryParams: Record<string, string | number> = {
        metric_name: params.metric,
        limit: params.limit,
    };
    if (params.category) queryParams.category = params.category;
    if (params.timePeriod) queryParams.time_period = params.timePeriod;

    const response = await api.get<GetSocialLeaderboardResponse>('social/leaderboard', {
        params: queryParams,
        signal,
    });
    return response.data.rank_list.map(
        (vo): SocialLeaderboardEntry => ({
            nickname: vo.nickname,
            rank: vo.rank,
            value: vo.value,
            isAnonymous: vo.is_anonymous,
            profileImagePath: vo.profile_image_path,
        }),
    );
}

type SocialLeaderboardQueryOptions = Partial<
    Omit<
        UseQueryOptions<
            SocialLeaderboardEntry[],
            Error,
            SocialLeaderboardEntry[],
            ReturnType<typeof socialLeaderboardKeys.list>
        >,
        'queryKey' | 'queryFn'
    >
>;

/**
 * Ranked leaderboard for a single metric (Volume, Profit, or Predictions). The
 * returned domain instances match the shape `MetricLeaderboard` already
 * consumes via the `ranking` prop.
 */
export function useSocialLeaderboard(
    params: SocialLeaderboardParams,
    options?: SocialLeaderboardQueryOptions,
): UseQueryResult<SocialLeaderboardEntry[], Error> {
    return useQuery({
        queryKey: socialLeaderboardKeys.list(params),
        queryFn: ({ signal }) => fetchSocialLeaderboard(params, signal),
        refetchInterval: REFETCH_INTERVAL_MS,
        staleTime: STALE_TIME_MS,
        retry: 2,
        ...options,
    });
}

type SocialLeaderboardRankQueryOptions = Partial<
    Omit<
        UseQueryOptions<
            SocialLeaderboardUserRank,
            Error,
            SocialLeaderboardUserRank,
            ReturnType<typeof socialLeaderboardKeys.userRank>
        >,
        'queryKey' | 'queryFn'
    >
>;

export function useSocialLeaderboardUserRank(
    params: SocialLeaderboardRankParams,
    options?: SocialLeaderboardRankQueryOptions,
): UseQueryResult<SocialLeaderboardUserRank, Error> {
    const userId = useAuthStore((state) => state.userId);

    return useQuery({
        queryKey: socialLeaderboardKeys.userRank(userId ?? '', params),
        queryFn: async ({ signal }) => {
            const response = await api.get<GetSocialLeaderboardRankResponse>(
                `users/${userId}/social/leaderboard_rank`,
                {
                    params: {
                        metric_name: params.metric,
                        ...(params.category ? { category: params.category } : {}),
                        ...(params.timePeriod ? { time_period: params.timePeriod } : {}),
                    },
                    signal,
                },
            );
            const rankItem = response.data.rank_item;

            return {
                isRanked: response.data.ranked,
                rank: rankItem?.rank ?? null,
                value: rankItem?.value ?? null,
            };
        },
        refetchInterval: REFETCH_INTERVAL_MS,
        staleTime: STALE_TIME_MS,
        retry: 2,
        ...options,
        enabled: !!userId && (options?.enabled ?? true),
    });
}

/* -------------------------------------------------------------------------- */
/*                          SERVER PREFETCH                                   */
/* -------------------------------------------------------------------------- */

/**
 * One leaderboard's prefetched payload paired with the params used to fetch it.
 * The params let consumers seed the cache under the same query key the client
 * hook will read.
 */
export interface SocialLeaderboardSeed {
    params: SocialLeaderboardParams;
    data: SocialLeaderboardEntry[];
}

async function fetchSocialLeaderboardServer(params: SocialLeaderboardParams): Promise<SocialLeaderboardEntry[] | null> {
    const queryParams = new URLSearchParams({
        metric_name: params.metric,
        limit: String(params.limit),
    });
    if (params.category) queryParams.set('category', params.category);
    if (params.timePeriod) queryParams.set('time_period', params.timePeriod);

    const response = await fetchServerSide(`${config.exchangeApiUrl}/v1/social/leaderboard?${queryParams.toString()}`, {
        next: { revalidate: 60 },
    });
    // Return null (not []) on transient failure so the client-side query
    // doesn't get seeded with empty success data — that would suppress the
    // loading/error UI and defer recovery until the next refetch interval.
    if (!response?.ok) return null;
    const json = (await response.json()) as GetSocialLeaderboardResponse;
    return json.rank_list.map(
        (vo): SocialLeaderboardEntry => ({
            nickname: vo.nickname,
            rank: vo.rank,
            value: vo.value,
            isAnonymous: vo.is_anonymous,
            profileImagePath: vo.profile_image_path,
        }),
    );
}

/**
 * Server-side fetch of the selected Leaderboard metric queries in parallel.
 * Perpetuals fetches Profit only; prediction categories fetch all three.
 * Returned seeds are passed as a prop to the client
 * tree, which seeds the React Query cache synchronously on first render — no
 * `HydrationBoundary` / `dehydrate` round-trip needed.
 *
 * Uses the project's `fetchServerSide` (plain `fetch` under the hood) so the
 * route does not need `connection()` to opt out of static rendering.
 */
export interface SocialLeaderboardPrefetchOptions {
    limit?: number;
    category?: string;
    timePeriod?: LeaderboardTimePeriod;
}

export function buildSocialLeaderboardPrefetchParams({
    limit,
    category,
    timePeriod = 'weekly',
}: SocialLeaderboardPrefetchOptions = {}): SocialLeaderboardParams[] {
    const metrics: LeaderboardMetricName[] =
        category === 'perps' ? ['projected_pnl'] : ['projected_pnl', 'volume', 'num_markets_traded'];
    const resolvedLimit = limit ?? (category === 'perps' ? PERPS_LEADERBOARD_LIMIT : SOCIAL_LEADERBOARD_LIMIT);

    return metrics.map((metric) => ({ metric, limit: resolvedLimit, category, timePeriod }));
}

export async function prefetchSocialLeaderboard(
    options: SocialLeaderboardPrefetchOptions = {},
): Promise<SocialLeaderboardSeed[]> {
    const paramsList = buildSocialLeaderboardPrefetchParams(options);
    const seeds = await Promise.all(
        paramsList.map(async (params): Promise<SocialLeaderboardSeed | null> => {
            const data = await fetchSocialLeaderboardServer(params);
            // Drop failed metrics so the client falls back to its loading
            // state and refetches via the standard `useSocialLeaderboard` flow.
            if (data === null) return null;
            return { params, data };
        }),
    );
    return seeds.filter((seed): seed is SocialLeaderboardSeed => seed !== null);
}

/**
 * Seeds prefetched leaderboard data into the React Query cache. Call this from
 * a client component synchronously on first render so consumer hooks read
 * cached data on their initial mount.
 */
export function setSocialLeaderboardData(queryClient: QueryClient, seeds: SocialLeaderboardSeed[]): void {
    for (const { params, data } of seeds) {
        queryClient.setQueryData(socialLeaderboardKeys.list(params), data);
    }
}
